import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, QrCode, MapPin, Umbrella, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import TopBar from '../components/common/TopBar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import { orderService, paymentService } from '../services/api';
import api from '../services/api';
import { toast } from 'sonner';

const Checkout = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { cart, kiosk, getTotal, clearCart } = useCart();
  const { user } = useAuth();
  const { location, requestLocation } = useLocation();

  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [reference, setReference] = useState('');
  const [umbrellaColor, setUmbrellaColor] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  const subtotal = getTotal();
  const discount = appliedCoupon?.discount_amount || 0;
  const total = Math.max(0, subtotal - discount);

  // Guard against empty cart (use useEffect to avoid render-time navigate)
  useEffect(() => {
    if (cart.length === 0 && !loading && !orderConfirmed) {
      navigate('/cart', { replace: true });
    }
  }, [cart.length, loading, orderConfirmed, navigate]);

  if ((cart.length === 0 && !orderConfirmed) || !kiosk) {
    return null;
  }

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const { data } = await api.post(`/coupons/validate/${couponCode.toUpperCase()}`, null, {
        params: { order_value: subtotal },
      });
      setAppliedCoupon(data);
      toast.success(`Cupom aplicado! -R$ ${data.discount_amount.toFixed(2)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cupom inválido');
      setAppliedCoupon(null);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const handleConfirmOrder = async () => {
    if (!reference.trim()) {
      toast.error('Informe um ponto de referência para a entrega');
      return;
    }

    setLoading(true);

    try {
      // Use user location if available, otherwise fallback to kiosk location
      const coords = location
        ? [location.longitude, location.latitude]
        : kiosk.location.coordinates;

      // Create order
      const orderData = {
        kiosk_id: kiosk.id,
        customer_id: user.id,
        items: cart,
        subtotal: subtotal,
        delivery_fee: 0.0,
        total: total,
        coupon_code: appliedCoupon ? couponCode.toUpperCase() : null,
        discount_amount: discount,
        delivery_location: {
          location: {
            type: 'Point',
            coordinates: coords,
          },
          reference: reference,
          umbrella_color: umbrellaColor,
          other_reference: notes,
        },
        notes: notes,
      };

      const order = await orderService.create(orderData);
      toast.success('Pedido criado!');

      // Create payment
      const payment = await paymentService.createPayment(order.id, paymentMethod);
      toast.success('Pagamento iniciado!');

      // Mark as confirmed to prevent empty-cart redirect, then navigate, then clear cart
      setOrderConfirmed(true);
      navigate(`/order/${order.id}`);
      clearCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar pedido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-32" data-testid="checkout-page">
      <TopBar title={t('checkout.title')} showBack />

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Order Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-4">
          <h2 className="font-['Outfit'] font-bold text-lg mb-3">{t('checkout.summary')}</h2>
          <div className="space-y-2 mb-3">
            {cart.map((item) => (
              <div key={item.product_id} className="flex justify-between text-sm">
                <span>{item.quantity}x {item.product_name}</span>
                <span className="font-semibold">R$ {item.subtotal.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border pt-2 space-y-1">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{t('cart.subtotal')}</span>
              <span>R$ {subtotal.toFixed(2)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-sm text-secondary">
                <span>{t('cart.discount')} ({couponCode})</span>
                <span>-R$ {discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-base pt-1 border-t border-border">
              <span>{t('cart.total')}</span>
              <span className="text-primary">R$ {total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Coupon */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-4">
          <h2 className="font-['Outfit'] font-bold text-lg mb-3 flex items-center gap-2">
            <Tag className="w-5 h-5 text-primary" />
            {t('checkout.couponCode')}
          </h2>
          {appliedCoupon ? (
            <div className="flex items-center justify-between bg-secondary/10 border border-secondary/20 rounded-xl p-3">
              <div>
                <p className="font-bold text-secondary">{couponCode}</p>
                <p className="text-xs text-muted-foreground">{appliedCoupon.description}</p>
                <p className="text-sm font-bold mt-1">-R$ {appliedCoupon.discount_amount.toFixed(2)}</p>
              </div>
              <button
                data-testid="remove-coupon"
                onClick={removeCoupon}
                className="text-sm text-destructive font-semibold"
              >
                {t('common.delete')}
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                data-testid="checkout-coupon-input"
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder={t('checkout.couponCode')}
                className="flex-1 px-3 py-2 border border-border rounded-full text-sm uppercase focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                data-testid="apply-coupon"
                onClick={applyCoupon}
                disabled={!couponCode.trim()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-full text-sm font-semibold disabled:opacity-50 active:scale-95"
              >
                {t('checkout.applyCoupon')}
              </button>
            </div>
          )}
        </div>

        {/* Delivery Info */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-4">
          <h2 className="font-['Outfit'] font-bold text-lg mb-3 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-secondary" />
            {t('checkout.where')}
          </h2>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('checkout.reference')} *
              </label>
              <input
                data-testid="reference-input"
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={t('checkout.referencePlaceholder')}
                className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                <Umbrella className="w-4 h-4 inline mr-1" />
                {t('checkout.umbrellaColor')}
              </label>
              <input
                data-testid="umbrella-color-input"
                type="text"
                value={umbrellaColor}
                onChange={(e) => setUmbrellaColor(e.target.value)}
                placeholder={t('checkout.umbrellaPlaceholder')}
                className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('checkout.notes')}
              </label>
              <textarea
                data-testid="notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('checkout.notesPlaceholder')}
                rows={2}
                className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            {!location && (
              <button
                onClick={requestLocation}
                type="button"
                className="w-full py-2 bg-secondary/10 text-secondary rounded-xl font-semibold flex items-center justify-center gap-2"
              >
                <MapPin className="w-4 h-4" />
                {t('checkout.enableLocation')}
              </button>
            )}
            {!location && (
              <p className="text-xs text-muted-foreground">
                {t('checkout.locationFallback')}
              </p>
            )}
            {location && (
              <p className="text-xs text-secondary flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {t('checkout.locationActive')}
              </p>
            )}
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-4">
          <h2 className="font-['Outfit'] font-bold text-lg mb-3">{t('checkout.howToPay')}</h2>
          <div className="space-y-2">
            <button
              data-testid="pay-pix"
              onClick={() => setPaymentMethod('pix')}
              className={`w-full p-3 rounded-xl border-2 flex items-center gap-3 transition-colors ${
                paymentMethod === 'pix'
                  ? 'border-primary bg-primary/5'
                  : 'border-border'
              }`}
            >
              <QrCode className="w-6 h-6 text-primary" />
              <div className="text-left flex-1">
                <p className="font-semibold">{t('checkout.pix')}</p>
                <p className="text-xs text-muted-foreground">{t('checkout.pixSub')}</p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 ${
                paymentMethod === 'pix' ? 'border-primary bg-primary' : 'border-border'
              }`} />
            </button>

            <button
              data-testid="pay-credit"
              onClick={() => setPaymentMethod('credit_card')}
              className={`w-full p-3 rounded-xl border-2 flex items-center gap-3 transition-colors ${
                paymentMethod === 'credit_card'
                  ? 'border-primary bg-primary/5'
                  : 'border-border'
              }`}
            >
              <CreditCard className="w-6 h-6 text-primary" />
              <div className="text-left flex-1">
                <p className="font-semibold">{t('checkout.creditCard')}</p>
                <p className="text-xs text-muted-foreground">{t('checkout.creditCardSub')}</p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 ${
                paymentMethod === 'credit_card' ? 'border-primary bg-primary' : 'border-border'
              }`} />
            </button>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 max-w-md mx-auto p-4 bg-white border-t border-border z-40">
        <button
          data-testid="confirm-order"
          onClick={handleConfirmOrder}
          disabled={loading}
          className="w-full py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:opacity-90 transition-opacity active:scale-95 shadow-lg disabled:opacity-50"
        >
          {loading ? t('checkout.processing') : `${t('checkout.confirmOrder')} • R$ ${total.toFixed(2)}`}
        </button>
      </div>
    </div>
  );
};

export default Checkout;
